import { getJSON } from '@folklore/fetch';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isArray } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const useItems = ({
    store,
    url,
    paginated: initialPaginated = true,
    query = null,
    onQueryChange,
    queryConfig = null,
    keepData = true,
    ...oldProps
}) => {
    const pages = useRef(null);

    // Legacy compatibility
    const { getPage = null, pages: initialPages = null } = oldProps || {};

    const paginated = useMemo(
        () => getPage !== null || initialPages !== null || initialPaginated,
        [getPage, initialPages, initialPaginated],
    );

    const { page = null, count = null, ...queryWithoutPageAndCount } = query || {};

    const queryParamsKey = Object.keys(queryWithoutPageAndCount || {}).reduce((acc, key) => {
        const it = queryWithoutPageAndCount[key] || null;
        if (it !== null) {
            if (isArray(it) && it.length > 0) {
                return `${acc}-${key}-${it.reduce((acc2, sub) => {
                    if (sub !== null && sub !== '') {
                        return acc2 === null ? sub : `${acc2}-${sub}`;
                    }
                    return acc2;
                }, null)}`;
            }
            if (!isArray(it) && it !== null && it !== '') {
                return acc === null ? `${key}-${it}` : `${acc}-${key}-${it}`;
            }
            return acc;
        }
        return acc;
    }, store);

    // Reset pages on query change...
    useEffect(() => {
        pages.current = {};
    }, [queryParamsKey]);

    const queryKey = useMemo(
        () =>
            paginated && getPage !== null
                ? [store, page, count, queryWithoutPageAndCount]
                : [store, query],
        [store, paginated, page, count, queryParamsKey],
    );

    const {
        data = null,
        refetch: reload,
        isLoading,
        isFetching,
        isRefetching,
        isFetched,
        status = null,
        error = null,
        ...otherProps
    } = useQuery({
        queryKey,
        queryFn: ({ queryKey: key }) => {
            if (paginated && getPage !== null) {
                const [, p = null, c = null, q = null] = key;
                return getPage(p, c, q);
            }
            const [, q = null] = key;
            return getJSON(
                `${url}${
                    !isEmpty(q) ? `?${queryString.stringify(q, { arrayFormat: 'bracket' })}` : ''
                }`,
            );
        },
        ...(keepData ? { placeholderData: keepPreviousData } : null),
        // ...(providedItems !== null ? { initialData: providedItems } : null),
        ...queryConfig,
        // refetchOnMount: 'always',
    });

    const { data: items = [], pagination = null, meta = null } = data || {};
    const {
        page: currentPage = null,
        last_page: lastPage = null,
        total = null,
    } = pagination || meta || {};

    const loadNextPage = useCallback(() => {
        if (onQueryChange !== null && paginated && page !== null && page < lastPage) {
            onQueryChange({ ...query, page: parseInt(page, 10) + 1 });
        }
    }, [paginated, pagination, page]);

    const loadPage = useCallback(
        (newPage) => {
            if (onQueryChange !== null && newPage !== null) {
                onQueryChange({ ...query, page: parseInt(newPage, 10) });
            }
        },
        [query, onQueryChange],
    );

    const finalItems = useMemo(() => {
        if (data === null) {
            return data;
        }
        if (paginated) {
            return items;
        }
        return data;
    }, [items, data, paginated]);

    if (isFetched && page !== null && data !== null) {
        pages.current = {
            ...pages.current,
            [page]: data,
        };
    }

    const allItems =
        pages.current !== null
            ? Object.keys(pages.current).flatMap((k) => pages.current[k]?.data)
            : null;

    const finalLoading = isLoading || isFetching || isRefetching;

    return {
        ...otherProps,
        items: finalItems,
        allItems,
        pages: pages.current,
        pageItems: paginated ? finalItems : null,
        pagination: paginated ? { page: currentPage, lastPage, total } : null,
        total,
        lastPage,
        loaded: isFetched,
        allLoaded:
            (paginated &&
                pages.current !== null &&
                Object.keys(pages.current).length === lastPage) ||
            (!paginated && isFetched), // very basic stuff, TODO
        loading: finalLoading,
        loadNextPage,
        loadPage,
        reloadPage: reload,
        reload,
        reset: reload,
        status,
        error,
    };
};

export default useItems;
